import * as React from 'react';
import RenderHelpers from '../../helpers/RenderHelpers';
import styles from './StackContainer.module.css';
import {
  CSSProperties,
  KeyboardEventHandler,
  LegacyRef,
  MouseEventHandler,
  PropsWithChildren,
} from 'react';
import {Position} from '../../types/Position';
import {HorizontalAlignment} from '../../types/HorizontalAlignment';
import {VerticalAlignment} from '../../types/VerticalAlignment';
import Helpers from '../../../shared-common/helpers/Helpers';
import {flatten} from 'lodash-es';
import {Dimension} from '../../../reports/types/Dimension';

export interface StackContainerProps {
  id?: string;
  className?: string;
  style?: CSSProperties;
  attributes?: unknown;
  divRef?: LegacyRef<HTMLDivElement>;
  stackFrom?: Position;
  horizontalAlignment?: HorizontalAlignment;
  verticalAlignment?: VerticalAlignment;
  gapWidth?: number | string;
  gapClassName?: string;
  showBottomGap?: boolean;
  onClick?: MouseEventHandler<HTMLDivElement>;
  onDoubleClick?: MouseEventHandler<HTMLDivElement>;
  onMouseDown?: MouseEventHandler<HTMLDivElement>;
  onMouseUp?: MouseEventHandler<HTMLDivElement>;
  onMouseOver?: MouseEventHandler<HTMLDivElement>;
  onMouseOut?: MouseEventHandler<HTMLDivElement>;
  onKeyUpCapture?: KeyboardEventHandler<HTMLDivElement>;
}

export const StackContainer = (props: PropsWithChildren<StackContainerProps>) => {
  const stackFrom = Helpers.ifBlank(props.stackFrom, 'top');
  const gapWidth = Dimension.inPixels(props.gapWidth).asCss();
  const showBottomGap = Helpers.ifNil(props.showBottomGap, false);

  const hasNoGapWidth = (typeof gapWidth === 'string' && Helpers.isBlank(gapWidth)) || gapWidth === '0';
  const hasNoGapClassName = Helpers.isBlank(props.gapClassName);

  const shouldSpaceChildren = !hasNoGapWidth || !hasNoGapClassName || showBottomGap;

  let spacedChildren: unknown[];

  if (shouldSpaceChildren) {
    let spacingWidth: number | string | undefined;
    let spacingHeight: number | string | undefined;

    if (stackFrom === 'left' || stackFrom === 'right') {
      spacingWidth = gapWidth;
      spacingHeight = '100%';
    } else {
      spacingWidth = '100%';
      // noinspection JSSuspiciousNameCombination
      spacingHeight = gapWidth;
    }

    spacedChildren =
      flatten(
        React.Children.toArray(props.children)
          .map((c, i) =>
            [
              c,
              <div
                key={i}
                className={
                  RenderHelpers.classes(
                    styles.gap,
                    props.gapClassName
                  )
                }
                style={{width: spacingWidth, height: spacingHeight}}
              />
            ])
      );
  } else {
    spacedChildren = React.Children.toArray(props.children);
  }

  return (
    <div
      ref={props.divRef}
      id={props.id}
      className={RenderHelpers.classes(
        styles.container,
        props.className,
        showBottomGap ? styles.withBottomGap : null,
        RenderHelpers.switch(
          stackFrom,
          {when: 'left', then: styles.stackFromLeft},
          {when: 'top', then: styles.stackFromTop},
          {when: 'right', then: styles.stackFromRight},
          {when: 'bottom', then: styles.stackFromBottom}
        ),
        RenderHelpers.switch(
          props.horizontalAlignment,
          {when: 'left', then: styles.horizontalAlignLeft},
          {when: 'center', then: styles.horizontalAlignCenter},
          {when: 'right', then: styles.horizontalAlignRight},
          {then: styles.horizontalAlignStretch}
        ),
        RenderHelpers.switch(
          props.verticalAlignment,
          {when: 'top', then: styles.verticalAlignTop},
          {when: 'center', then: styles.verticalAlignCenter},
          {when: 'bottom', then: styles.verticalAlignBottom},
          {then: styles.verticalAlignStretch}
        )
      )}
      style={props.style}
      {...props.attributes}
      onClick={props.onClick}
      onDoubleClick={props.onDoubleClick}
      onMouseDown={props.onMouseDown}
      onMouseUp={props.onMouseUp}
      onMouseOver={props.onMouseOver}
      onMouseOut={props.onMouseOut}
      onKeyUpCapture={props.onKeyUpCapture}
    >
      {spacedChildren}
    </div>
  );
};